import React from 'react';

import PropTypes from 'prop-types';

import { RenderIconType } from '@adsk/alloy-react-icon';
import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType } from '@adsk/alloy-react-theme';

import BasicButton from './BasicButton';
import { buttonTypePropType, StylableButtonProps } from '../consts';

const id = 'icon-button';

type IconProps = {
  size: number;
  id: string;
  'aria-hidden': boolean;
  'aria-label'?: string;
};

/**
 * Icon Button.
 */
const IconButton = React.forwardRef<
  HTMLButtonElement,
  StylableButtonProps & {
    renderIcon?: RenderIconType;
    size?: number;
  }
>(
  (
    {
      style,
      className,
      children,
      size = 24,
      renderIcon,
      'aria-label': ariaLabel,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const button = theme.tokens?.component?.button;

    const iconProps: IconProps = {
      size,
      id,
      'aria-hidden': false,
    };

    if (ariaLabel) {
      // overwrites the icon's default aria-label
      iconProps['aria-label'] = ariaLabel;
    }

    const iconColor = button?.outlined?.icon?.fill;
    const iconColorHover = button?.outlined?.hover?.icon?.fill;
    const iconColorActive = button?.outlined?.active?.icon?.fill;
    const iconColorFocus = button?.outlined?.focus?.icon?.fill;
    const iconColorDisabled = button?.outlined?.disabled?.icon?.fill;

    return (
      <BasicButton
        ref={ref}
        className={className}
        aria-labelledby={id}
        style={[
          {
            borderRadius: button?.borderRadius,
            padding: 0,
            width: size,
            height: size,
            color: iconColor,
            '&:hover': {
              color: iconColorHover,
            },
            '&:active': {
              color: iconColorActive,
            },
            '&:focus': {
              color: iconColorFocus,
            },
            '&[disabled]': {
              color: iconColorDisabled,
              cursor: 'not-allowed',
            },
          },
          style,
        ]}
        {...props}
      >
        {children || renderIcon?.(iconProps)}
      </BasicButton>
    );
  },
);

IconButton.displayName = 'IconButton';

IconButton.propTypes = {
  /** BasicButton's content */
  children: PropTypes.any,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** button html type, eg: submit */
  type: buttonTypePropType,
  /** Icon size, passed only when using `iconType` */
  size: PropTypes.number,
  /** Icon type to display on left */
  renderIcon: PropTypes.func,
};

export default IconButton;
